import { ArrowRight, Check, Loader2, Users2Icon } from "lucide-react";
import { useState } from "react";
import useSWR, { useSWRConfig } from "swr";

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useSession } from "@/components/useSession";
import logger from "@/lib/logger";
import { cn, fetcherTyped, formatMessage, okstatus } from "@/lib/utils";
import { TenantMembershipListSchema } from "@/types/schemas";
import Link from "next/link";

export const TenantSwitcher = () => {
  const { update } = useSession();
  const { mutate } = useSWRConfig();
  const { data: tenants } = useSWR("/api/tenant", fetcherTyped(TenantMembershipListSchema));
  const [switching, setSwitching] = useState<string | null>(null);

  if (!tenants) {
    return null;
  }

  const handleSwitch = async (tenantId: string) => {
    if (switching) {
      return;
    }

    setSwitching(tenantId);
    try {
      await fetch("/api/tenant", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId })
      })
        .then(res => okstatus(res))
        .then(res => res.json());

      await update();
      await mutate(() => true, undefined, { revalidate: true });
    } catch (error) {
      logger.warn("Failed to switch tenant", formatMessage(error));
    } finally {
      setSwitching(null);
    }
  };

  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        My Teams
      </DropdownMenuLabel>

      {tenants.map(tenant => (
        <DropdownMenuItem
          key={tenant.tenantId}
          className="gap-2"
          disabled={!!switching}
          onSelect={event => {
            event.preventDefault();
            handleSwitch(tenant.tenantId);
          }}
        >
          <Users2Icon />
          <span className="truncate">{tenant.name}</span>
          {switching === tenant.tenantId ? (
            <Loader2 className="ml-auto h-4 w-4 animate-spin" />
          ) : (
            <Check
              className={cn("ml-auto h-4 w-4", tenant.current ? "opacity-100" : "opacity-0")}
            />
          )}
        </DropdownMenuItem>
      ))}

      <DropdownMenuItem key="all-teams" className="gap-2" disabled={!!switching} asChild>
        <Link href="/team">
          <ArrowRight />
          <span className="truncate">Choose or create a team</span>
        </Link>
      </DropdownMenuItem>

      <DropdownMenuSeparator />
    </>
  );
};
